
import React from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';

interface BarChartProps {
  title: string;
  data: Array<Record<string, unknown>>;
  bars: {
    name: string;
    dataKey: string;
    color: string;
  }[];
  yAxisLabel?: string;
  xAxisLabel?: string;
  description?: string;
}

export function BarChart({
  title,
  data,
  bars,
  yAxisLabel,
  xAxisLabel,
  description,
}: BarChartProps) {
  const categories = (data || []).map((d) => d?.name);

  const series = bars.map((bar) => ({
    name: bar.name,
    data: (data || []).map((d) => {
      const v = d?.[bar.dataKey];
      return typeof v === 'number' ? v : Number(v) || 0;
    }),
  }));

  const stacked = bars.length > 1;

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      stacked,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: {
        enabled: true,
        speed: 1500,
      },
      fontFamily: 'inherit',
    },
    colors: bars.map((b) => b.color),
    plotOptions: {
      bar: {
        borderRadius: 2,
        columnWidth: '60%',
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: stacked ? 0 : 1,
      colors: ['transparent'],
    },
    grid: {
      strokeDashArray: 3,
      borderColor: 'rgba(148, 163, 184, 0.3)',
      padding: { top: 0, right: 30, left: 20, bottom: 20 },
    },
    xaxis: {
      categories,
      labels: { style: { fontSize: '12px' } },
      // ApexCharts crashes on undefined title — always pass an object.
      title: { text: xAxisLabel ?? '', offsetY: 10 },
    },
    yaxis: {
      labels: { style: { fontSize: '12px' } },
      title: { text: yAxisLabel ?? '', style: { fontSize: '12px' } },
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="h-80 w-full">
          <Chart
            options={options}
            series={series}
            type="bar"
            height="100%"
            width="100%"
          />
        </div>
      </CardContent>
    </Card>
  );
}
